import NoteAddIcon from '@mui/icons-material/NoteAdd'
import SaveIcon from '@mui/icons-material/Save'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { FC, useEffect, useState } from 'react'
import { materialTypeValues } from '../../api/appAPI'
import emptyMaterial from '../../assets/emptyMaterial.png'
import {
  InputWithoutSubmitWithUnitSupport,
  InputWithoutSubmitWithWeightUnitSupport,
} from '../common/UnitComponents'
import QuestionIcon from '@mui/icons-material/HelpOutline'

interface ITypeButtonProps {
  title: string
  selectedType: string
  type: string
  children: JSX.Element

  setType(type: string): void
}

const TypeButton: FC<ITypeButtonProps> = ({
  title,
  selectedType,
  type,
  children,
  setType,
}) => {
  return (
    <Tooltip title={title}>
      <Box
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 100,
          width: 100,
          cursor: 'pointer',
        }}
        onClick={() => {
          setType(type)
        }}
      >
        <Box
          style={{
            height: '100%',
            width: '100%',
            border: `${selectedType === type ? 4 : 2}px solid ${
              selectedType === type ? '#3f51b5' : '#616161'
            }`,
            borderRadius: 8,
          }}
        >
          {React.cloneElement(children, {
            width: '100%',
            height: '100%',
            viewBox: '0 0 100 100',
          })}
        </Box>
      </Box>
    </Tooltip>
  )
}

type TextfieldProps = {
  value: string
  onChange(event: React.ChangeEvent<{ value: unknown }>): void
}

type NumberTextfieldProps = {
  value: number
  onChange(v: number): void
}

type SelectProps = {
  value: string
  onChange(event: SelectChangeEvent<string>): void
}

interface IProps {
  title: string

  name: TextfieldProps
  displayName: TextfieldProps
  sku: TextfieldProps
  type: SelectProps
  cabinetDoor: {
    value: string
    set(v: string): void
  }
  thumbnailUrl: {
    value: string
    onChange(value: string): void
  }
  textureUrl: {
    value: string
    onChange(value: string): void
  }
  isTexture: {
    value: string
    set(v: string): void
  }
  length: NumberTextfieldProps
  width: NumberTextfieldProps
  description: TextfieldProps
  weight: NumberTextfieldProps
  price: TextfieldProps
  addon: TextfieldProps
  threeProps: TextfieldProps
  distributorCompany: SelectProps
  distributorCompanies: string[]
  permissions: {
    value: string
    set(v: string): void
  }

  button: {
    text: string
    onClick(): void
  }

  textures: string[]

  needSave?: boolean
  editable: boolean
}

const MaterialUIBox: FC<IProps> = (props) => {
  const [textureUrl, setTextureUrl] = useState(props.thumbnailUrl.value)
  const [chooseTextureOpen, setChooseTextureOpen] = useState(false)
  const [selectedTexture, setSelectedTexture] = useState(props.textureUrl.value)

  useEffect(() => {
    setTextureUrl(props.thumbnailUrl.value)
  }, [props.thumbnailUrl.value])

  useEffect(() => {
    setSelectedTexture(props.textureUrl.value)
  }, [props.textureUrl.value])

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      pb={2}
      style={{
        width: 400,
        height: 'fit-content',
        margin: 15,
        border: '1px solid black',
        maxHeight: '90%',
        overflowY: 'auto',
        overflowX: 'hidden',
        position: 'relative',
      }}
    >
      <Box
        py={1}
        style={{
          position: 'sticky',
          top: 0,
          width: '100%',
          backgroundColor: 'white',
          zIndex: 10,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={3}>
            {' '}
          </Grid>
          <Grid
            item
            xs={6}
            container
            justifyContent="center"
            alignItems="center"
          >
            <Typography variant="h6">{props.title}</Typography>
          </Grid>
          <Grid
            item
            xs={3}
            container
            justifyContent="flex-end"
            alignItems="center"
          >
            <Tooltip title={props.title === 'New Material' ? 'Create' : 'Save'}>
              <Fab
                color={props.needSave ? 'secondary' : 'primary'}
                size="small"
                onClick={props.button.onClick}
                style={{ marginRight: 10 }}
                disabled={!props.editable}
              >
                {props.title === 'New Material' ? (
                  <NoteAddIcon />
                ) : (
                  <SaveIcon />
                )}
              </Fab>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      <TextField
        label="Material Name"
        variant="outlined"
        value={props.name.value}
        onChange={props.name.onChange}
        style={{ width: '60%', marginTop: 15 }}
        disabled={!props.editable}
      />
      <TextField
        label="Material Display Name"
        variant="outlined"
        value={props.displayName.value}
        onChange={props.displayName.onChange}
        style={{ width: '60%', marginTop: 15 }}
        disabled={!props.editable}
      />
      <TextField
        label="Material SKU"
        variant="outlined"
        value={props.sku.value}
        onChange={props.sku.onChange}
        style={{ width: '60%', marginTop: 15 }}
        disabled={!props.editable}
      />
      <FormControl
        variant="outlined"
        style={{ width: '60%', marginTop: 15 }}
        disabled={!props.editable}
      >
        <InputLabel>Select type</InputLabel>
        <Select
          value={props.type.value}
          onChange={props.type.onChange}
          label="Select type"
        >
          {materialTypeValues.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {props.type.value === 'floor' && (
        <FormControl
          variant="outlined"
          style={{ width: '60%', marginTop: 15 }}
          disabled={!props.editable}
        >
          <InputLabel>Distributor Company</InputLabel>
          <Select
            value={props.distributorCompany.value}
            onChange={props.distributorCompany.onChange}
            label="Distributor Company"
          >
            {props.distributorCompanies.map((company) => (
              <MenuItem key={company} value={company}>
                {company}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <FormControl
        style={{ width: '60%', marginTop: 15 }}
        disabled={!props.editable}
      >
        <FormGroup row style={{ justifyContent: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={props.cabinetDoor.value[0] === '1'}
                onChange={(e) => {
                  let p = [...props.cabinetDoor.value]
                  p[0] = e.target.checked ? '1' : '0'
                  props.cabinetDoor.set(p.join(''))
                }}
              />
            }
            label="Cabinet"
            labelPlacement="top"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={props.cabinetDoor.value[1] === '1'}
                onChange={(e) => {
                  let p = [...props.cabinetDoor.value]
                  p[1] = e.target.checked ? '1' : '0'
                  props.cabinetDoor.set(p.join(''))
                }}
              />
            }
            label="Door"
            labelPlacement="top"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={props.cabinetDoor.value[2] === '1'}
                onChange={(e) => {
                  let p = [...props.cabinetDoor.value]
                  p[2] = e.target.checked ? '1' : '0'
                  props.cabinetDoor.set(p.join(''))
                }}
              />
            }
            label="DBox"
            labelPlacement="top"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={props.cabinetDoor.value[3] === '1'}
                onChange={(e) => {
                  let p = [...props.cabinetDoor.value]
                  p[3] = e.target.checked ? '1' : '0'
                  props.cabinetDoor.set(p.join(''))
                }}
              />
            }
            label="CTop"
            labelPlacement="top"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={props.cabinetDoor.value[4] === '1'}
                onChange={(e) => {
                  let p = [...props.cabinetDoor.value]
                  p[4] = e.target.checked ? '1' : '0'
                  props.cabinetDoor.set(p.join(''))
                }}
              />
            }
            label="Texture"
            labelPlacement="top"
          />
        </FormGroup>
      </FormControl>

      <Box
        mt={2}
        width={0.6}
        display="flex"
        justifyContent="space-around"
        alignItems="center"
      >
        <img
          src={textureUrl}
          alt="Logo Preview"
          width={50}
          onError={() => {
            setTextureUrl(emptyMaterial)
          }}
        />

        <Button
          variant="contained"
          onClick={() => {
            setChooseTextureOpen(true)
          }}
          disabled={!props.editable}
        >
          Browse
        </Button>
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            color="primary"
            checked={props.isTexture.value === '1'}
            onChange={(e) => {
              props.isTexture.set(e.target.checked ? '1' : '0')
            }}
          />
        }
        label="Is Texture"
        labelPlacement="start"
      />
      {/* <InputWithImageByLink
                label="Thumbnail Url"
                input={props.thumbnailUrl.input}
                setInput={props.thumbnailUrl.setInput}
                style={{ width: '60%', marginTop: 15 }}
                disabled={!props.editable}
            />
            <InputWithImageByLink
                label="Texture Url"
                input={props.textureUrl.input}
                setInput={props.textureUrl.setInput}
                style={{ width: '60%', marginTop: 15 }}
                disabled={!props.editable}
            /> */}
      <Box display="flex" mt={2} width="60%" gap={2}>
        <InputWithoutSubmitWithUnitSupport
          sx={{ flexGrow: 1 }}
          label="Texture Length"
          valueInCm={props.length.value}
          onChange={props.length.onChange}
          disabled={!props.editable}
        />
        <InputWithoutSubmitWithUnitSupport
          sx={{ flexGrow: 1 }}
          label="Texture Width"
          valueInCm={props.width.value}
          onChange={props.width.onChange}
          disabled={!props.editable}
        />
        {/* <FormControl variant="outlined" style={{ width: '47.5%' }} disabled={!props.editable}>
                    <InputLabel>Texture Length</InputLabel>
                    <OutlinedInput
                        label="Texture Length"
                        startAdornment={<InputAdornment position="start">cm</InputAdornment>}
                        type='number'
                        value={props.length.value}
                        onChange={props.length.onChange}
                    />
                </FormControl>
                <FormControl variant="outlined" style={{ width: '47.5%' }} disabled={!props.editable}>
                    <InputLabel>Texture Width</InputLabel>
                    <OutlinedInput
                        label="Texture Width"
                        startAdornment={<InputAdornment position="start">cm</InputAdornment>}
                        type='number'
                        value={props.width.value}
                        onChange={props.width.onChange}
                    />
                </FormControl> */}
      </Box>
      <TextField
        label="Material description"
        multiline
        minRows={3}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
          }
        }}
        variant="outlined"
        value={props.description.value}
        onChange={props.description.onChange}
        style={{ width: '60%', marginTop: 15 }}
        disabled={!props.editable}
      />
      {/* <FormControl variant="outlined" style={{ width: '60%', marginTop: 15 }} disabled={!props.editable}>
                <InputLabel>Material weight</InputLabel>
                <OutlinedInput
                    label="Material weight"
                    startAdornment={<InputAdornment position="start">kg</InputAdornment>}
                    type='number'
                    value={props.weight.value}
                    onChange={props.weight.onChange}
                />
            </FormControl> */}
      <InputWithoutSubmitWithWeightUnitSupport
        sx={{ width: 0.6, mt: 2 }}
        label="Material weight"
        valueInKg={props.weight.value}
        onChange={props.weight.onChange}
        disabled={!props.editable}
        endAdornment={
          <Tooltip
            placement="top"
            title={`In case this material will be attached to hardware which weight calculations are made depending on it's size, this weight will apply for per cm or per cm², regardless of the measurement unit you choose`}
          >
            <QuestionIcon color="secondary" />
          </Tooltip>
        }
      />
      <FormControl
        variant="outlined"
        style={{ width: '60%', marginTop: 15 }}
        disabled={!props.editable}
      >
        <InputLabel>Material price</InputLabel>
        <OutlinedInput
          label="Material price"
          startAdornment={<InputAdornment position="start">$</InputAdornment>}
          type="number"
          value={props.price.value}
          onChange={props.price.onChange}
          endAdornment={
            <Tooltip
              placement="top"
              title={`In case this material will be attached to hardware which price calculations are made depending on it's size, this price will apply for per cm or per cm², regardless of the measurement unit you choose. 
              Exception: for ctopLinear and ctopSquare types, which are being used in the design tool directly without hardwares, in these cases the price is for in or in²`}
            >
              <QuestionIcon color="secondary" />
            </Tooltip>
          }
        />
      </FormControl>
      <FormControl
        variant="outlined"
        style={{ width: '60%', marginTop: 15 }}
        disabled={!props.editable}
      >
        <InputLabel>Material addon</InputLabel>
        <OutlinedInput
          label="Material addon"
          startAdornment={<InputAdornment position="start">$</InputAdornment>}
          type="number"
          value={props.addon.value}
          onChange={props.addon.onChange}
          endAdornment={
            <Tooltip
              placement="top"
              title={`This plus material price will formulate unit price for calculation hardware price. Note: this value will not be included in clear price/cost calculation`}
            >
              <QuestionIcon color="secondary" />
            </Tooltip>
          }
        />
      </FormControl>

      <FormControl style={{ marginTop: 15 }} disabled={!props.editable}>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={props.permissions.value[0] === '1'}
                onChange={(e) => {
                  let p = [...props.permissions.value]
                  p[0] = e.target.checked ? '1' : '0'
                  props.permissions.set(p.join(''))
                }}
              />
            }
            label="Retail"
            labelPlacement="top"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={props.permissions.value[1] === '1'}
                onChange={(e) => {
                  let p = [...props.permissions.value]
                  p[1] = e.target.checked ? '1' : '0'
                  props.permissions.set(p.join(''))
                }}
              />
            }
            label="Dealer"
            labelPlacement="top"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={props.permissions.value[2] === '1'}
                onChange={(e) => {
                  let p = [...props.permissions.value]
                  p[2] = e.target.checked ? '1' : '0'
                  props.permissions.set(p.join(''))
                }}
              />
            }
            label="Designer"
            labelPlacement="top"
          />
        </FormGroup>
      </FormControl>

      <TextField
        label="Three Material Props"
        multiline
        minRows={3}
        variant="outlined"
        value={props.threeProps.value}
        onChange={props.threeProps.onChange}
        style={{ width: '60%', marginTop: 15 }}
        disabled={!props.editable}
      />

      <Dialog
        sx={{ '& .MuiDialog-paper': { width: '80%' } }}
        open={chooseTextureOpen}
      >
        <DialogTitle>Choose Texture</DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto auto auto auto auto',
              gridColumnGap: 5,
              gridRowGap: 15,
            }}
          >
            {props.textures.map((t) => (
              <TypeButton
                key={t}
                title={t}
                type={t}
                selectedType={selectedTexture}
                setType={(type: string) => {
                  setSelectedTexture(type)
                }}
              >
                <img
                  src={import.meta.env.VITE_TEXTURE_FOLDER_URL + t}
                  alt={t}
                  width={300}
                />
              </TypeButton>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            onClick={() => {
              setChooseTextureOpen(false)
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (selectedTexture && props.textures.includes(selectedTexture)) {
                props.textureUrl.onChange(selectedTexture)
              }
              setChooseTextureOpen(false)
            }}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default MaterialUIBox
